import { Badge, Divider, Group, Stack, Text } from "@mantine/core"
import { Card } from "@tfg/ui"
import _ from "lodash"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { Property } from "~/components/Property.tsx"
import { EditableCellWithTextInput } from "~/pages/Admin/Translations/columns.tsx"
import { CustomFieldProductsDetailsInfo } from "~/pages/Brand/Products/Details/CustomFieldGraph/CustomFieldProductsDetailsInfo"
import { MaterialPropertySelection } from "~/pages/Brand/Products/Details/MaterialPropertySelection.tsx"
import { useComponentUpdateNameMutation } from "~/pages/Brand/Products/Details/hooks.ts"
import { getCustomFieldDefinitions, getMainBranchOperations } from "~/pages/Brand/Products/Details/utils"
import { useAuth } from "~/store/auth-context.tsx"
import { useLocalizedTextByTextContent } from "~/utils/languages.tsx"

type Props = {
  component: LibraryResponses.EnrichedComponent
  product: LibraryResponses.ProductDetails
}

export const ComponentDetails = ({ component, product }: Props) => {
  const { t } = useTranslation("products")
  const { isAdmin } = useAuth()
  const { mutate: updateComponentName } = useComponentUpdateNameMutation()
  const getLocalizedText = useLocalizedTextByTextContent()

  const customFieldsDefs = getCustomFieldDefinitions(product, component)

  const localizedComponentName = getLocalizedText(component.typeTextContent)

  return (
    <Card>
      <Stack>
        <Group>
          {component.mainFabric && (
            <Badge size="xs" variant="info">
              {t("components.columns.mainFabric")}
            </Badge>
          )}
        </Group>
        <Group justify="space-between">
          <Text fz="xs">{t("components.columns.reference")}</Text>
          {isAdmin ? (
            <EditableCellWithTextInput
              key={component.uuid}
              defaultValue={localizedComponentName}
              onSave={(newValue: string) =>
                updateComponentName({
                  componentId: component.id,
                  name: newValue,
                })
              }
            />
          ) : (
            <Text>{localizedComponentName}</Text>
          )}
        </Group>
        <CustomFieldProductsDetailsInfo
          customFieldsDefs={customFieldsDefs}
          allCfv={getMainBranchOperations(component.item.operations).flatMap(
            (o) => o.customFieldValues
          )}
        />
        {!!component.materials?.length && (
          <Stack>
            <Divider />
            <Text fz="sm">{t("components.columns.composition")}</Text>
            <Stack gap="var(--tfg-molecule-padding-y)">
              {_.sortBy(component.materials, (child) => -child.proportion).map(
                (material) =>
                  isAdmin ? (
                    <MaterialPropertySelection
                      key={material.id}
                      material={material}
                    />
                  ) : (
                    <Property
                      key={material.id}
                      label={getLocalizedText(material.typeTextContent)}
                      value={`${material.proportion}%`}
                    />
                  )
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
